import {
  BarChart3,
  Bell,
  BotIcon,
  Calendar,
  Clock,
  Scissors,
  Settings,
  Star,
  Tag,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { useUnrepliedReviewCount } from "@/domains/reviews/hooks/use-unreplied-review-count";
import { AUTHORIZATION_ACTIONS, AUTHORIZATION_SUBJECTS } from "@/lib/authorization";
import { DashboardMode, useDashboardContext } from "@/lib/dashboard-context";
import { env } from "@/lib/env";
import { useSalonStore } from "@/lib/store/salon";
import { useConfigStoreSelectors } from "@/lib/store/use-config-store";
import { NavigationItem } from "../utils/navigation-items";

/**
 * Hook for getting localized navigation items based on dashboard context
 *
 * Reads config values from the Zustand config store, which is pre-populated
 * by useInitializeSalonConfigurations() in AppContainer before this component mounts.
 */
export const useNavigationItems = () => {
  const { t } = useTranslation("common");
  const { mode } = useDashboardContext();
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id ?? "";
  const unrepliedCount = useUnrepliedReviewCount();

  // Read from pre-fetched config store (populated by AppContainer before layout renders)
  const aiChatEnabled =
    useConfigStoreSelectors.useConfigurationValue(salonId, "premium_features.ai_chat_enabled") ??
    false;
  const reportEnabled =
    useConfigStoreSelectors.useConfigurationValue(salonId, "ui.report.enabled") ?? true;

  const getNavItems = (): NavigationItem[] => {
    const navItems: NavigationItem[] = [];

    if (mode === DashboardMode.WORKER) {
      // Worker navigation: focused on own work
      navItems.push({
        to: "/my-schedule",
        icon: Clock,
        label: t("navigation.mySchedule"),
      });
    } else if (mode === DashboardMode.MANAGER) {
      // Manager navigation: full business management
      navItems.push(
        {
          to: "/appointments",
          icon: Calendar,
          label: t("navigation.appointments"),
          badge: ({ attentionCount }) =>
            attentionCount > 0 ? (
              <Badge variant="destructive" className="ml-2 px-2 py-0.5 text-xs">
                {attentionCount}
              </Badge>
            ) : null,
        },
        {
          to: "/customer-service",
          icon: Star,
          label: t("navigation.customerService"),
          badge: () =>
            unrepliedCount > 0 ? (
              <Badge variant="destructive" className="ml-2 px-2 py-0.5 text-xs">
                {unrepliedCount}
              </Badge>
            ) : null,
        },
        {
          to: "/customers",
          icon: Users,
          label: t("navigation.customers"),
        },
        {
          to: "/services",
          icon: Scissors,
          label: t("navigation.services"),
        },
        {
          to: "/discounts",
          icon: Tag,
          label: t("navigation.discounts"),
        },
        // Reports
        {
          to: "/reports",
          icon: BarChart3,
          label: t("navigation.reports"),
          visible: reportEnabled,
        },
        {
          to: "/notifications",
          icon: Bell,
          label: t("navigation.notifications"),
          visible: env.flags.showExperimental,
          badge: ({ unreadCount }) =>
            unreadCount > 0 ? (
              <Badge variant="destructive" className="ml-2 px-2 py-0.5 text-xs">
                {unreadCount}
              </Badge>
            ) : null,
        },
        {
          to: "/settings",
          icon: Settings,
          label: t("navigation.settings"),
        }
      );
    }

    return navItems.filter((item) => item.visible !== false);
  };

  const getMobileNavItems = (): NavigationItem[] => {
    const mobileNavItems: NavigationItem[] = [
      // First tab - New Calendar (Dashboard) for managers, My Appointments for workers
      mode === DashboardMode.WORKER
        ? {
            to: "/my-schedule",
            icon: Clock,
            label: t("navigation.mySchedule"),
            permission: {
              action: AUTHORIZATION_ACTIONS.READ,
              subject: AUTHORIZATION_SUBJECTS.MY_APPOINTMENT,
            },
          }
        : {
            to: "/appointments",
            icon: Calendar,
            label: t("navigation.dashboard"),
            permission: {
              action: AUTHORIZATION_ACTIONS.READ,
              subject: AUTHORIZATION_SUBJECTS.APPOINTMENT,
            },
            badge: ({ attentionCount }) =>
              attentionCount > 0 ? (
                <Badge
                  variant="destructive"
                  className="absolute -top-2.5 -right-3 h-4.5 w-4.5 flex items-center justify-center text-xs"
                >
                  {attentionCount > 99 ? "99+" : attentionCount}
                </Badge>
              ) : null,
          },

      // Second tab - Customer Service (Reviews overview). Replaces the
      // standalone Customers tab on mobile per product direction: managers
      // want one-tap access to reviews on the go; the Customers list is
      // still reachable via the Salon settings page.
      {
        to: "/customer-service",
        icon: Star,
        // Short label so it fits one line in the mobile tab bar across locales.
        label: t("navigation.customerServiceShort"),
        permission: null,
        visible: mode === DashboardMode.MANAGER,
        badge: () =>
          unrepliedCount > 0 ? (
            <Badge
              variant="destructive"
              className="absolute -top-2.5 -right-3 h-4.5 w-4.5 flex items-center justify-center text-xs"
            >
              {unrepliedCount > 99 ? "99+" : unrepliedCount}
            </Badge>
          ) : null,
      },

      // Third tab - AI Assistant (center position)
      {
        to: "/ai-assistant",
        icon: BotIcon,
        label: "AI",
        permission: null,
        visible: aiChatEnabled,
      },

      // Fourth tab - Reports
      {
        to: "/reports",
        icon: BarChart3,
        label: t("navigation.reports"),
        permission: null, // Available to all users
        visible: mode === DashboardMode.MANAGER && reportEnabled,
      },

      // Fifth tab - Salon (includes settings access)
      {
        to: "/salon",
        icon: Settings,
        label: t("navigation.settings"),
        permission: null, // No specific permission needed to view salon page
      },
    ];

    // Filter visible items and return max 5 items for mobile
    return mobileNavItems.filter((item) => item.visible !== false).slice(0, 5);
  };

  return {
    getNavItems,
    getMobileNavItems,
    mode, // Expose dashboard mode for consumers
  };
};
